import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { QuestionDetailSheet } from "@/components/questions/question-detail-sheet";
import { ErrorState } from "@/components/shared/error-state";
import { PageHeader } from "@/components/shared/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import { type QuestionPriority, type QuestionRow, useQuestions } from "@/hooks/use-questions";
import { cn } from "@/lib/utils";

const SKELETON_KEYS = ["a", "b", "c", "d"] as const;

const PRIORITY_TINTS: Record<QuestionPriority, string> = {
  high: "bg-warning-bg text-warning",
  med: "bg-info-bg text-info",
  low: "bg-muted text-foreground/60",
};

const PRIORITY_RANK: Record<QuestionPriority, number> = {
  high: 0,
  med: 1,
  low: 2,
};

type SortKey =
  | "id"
  | "priority"
  | "status"
  | "date"
  | "text"
  | "raisedBy"
  | "assignedTo"
  | "related";
type SortDir = "asc" | "desc";

export function QuestionsPage() {
  const { solutionId } = useParams({ from: "/s/$solutionId" });
  const search = useSearch({ from: "/s/$solutionId/questions" });
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useQuestions(solutionId);
  const [filter, setFilter] = useState<"open" | "answered" | "all">("open");
  const [openId, setOpenId] = useState<string | null>(search.q ?? null);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  // Sync from URL → state when arriving with ?q=<id>
  useEffect(() => {
    if (search.q && search.q !== openId) setOpenId(search.q);
  }, [search.q, openId]);

  function handleOpenChange(open: boolean) {
    if (!open) {
      setOpenId(null);
      // Clear ?q= when the sheet closes so the URL stays clean
      if (search.q) {
        navigate({
          to: "/s/$solutionId/questions",
          params: { solutionId },
          search: (prev) => ({ ...prev, q: undefined }),
          replace: true,
        });
      }
    }
  }

  const filtered = useMemo(
    () => data?.filter((q) => filter === "all" || q.status === filter) ?? [],
    [data, filter],
  );

  const sorted = useMemo(
    () => sortQuestions(filtered, sortKey, sortDir),
    [filtered, sortKey, sortDir],
  );

  function toggleSort(k: SortKey) {
    if (sortKey === k) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(k);
      setSortDir("asc");
    }
  }

  return (
    <div className="mx-auto max-w-7xl px-6 py-8 space-y-6">
      <PageHeader
        title="Questions"
        description="Open questions raised by stakeholders or the AI. Assign, answer, resolve."
      />

      <div className="flex gap-1.5">
        {(
          [
            { id: "open", label: "Open" },
            { id: "answered", label: "Answered" },
            { id: "all", label: "All" },
          ] as const
        ).map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            className={cn(
              "rounded-full px-3 py-1 text-xs font-medium transition-colors",
              filter === f.id
                ? "bg-nymbl-navy text-white"
                : "bg-muted text-foreground/70 hover:bg-muted/80",
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading && (
        <ul className="space-y-2">
          {SKELETON_KEYS.map((k) => (
            <Skeleton key={k} className="h-10 rounded-md" />
          ))}
        </ul>
      )}

      {isError && <ErrorState message="Failed to load questions" onRetry={() => refetch()} />}

      {data && sorted.length === 0 && !isLoading && (
        <div className="rounded-xl border border-dashed border-[var(--color-border)] p-12 text-center">
          <p className="text-sm text-muted-foreground">No questions match this filter.</p>
        </div>
      )}

      {sorted.length > 0 && (
        <div className="rounded-xl border border-[var(--color-border)] bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold border-b border-[var(--color-border)]">
                  <Th className="w-10 text-right">#</Th>
                  <SortableTh
                    label="ID"
                    width="w-20"
                    active={sortKey === "id"}
                    dir={sortDir}
                    onClick={() => toggleSort("id")}
                  />
                  <SortableTh
                    label="Priority"
                    width="w-24"
                    active={sortKey === "priority"}
                    dir={sortDir}
                    onClick={() => toggleSort("priority")}
                  />
                  <SortableTh
                    label="Status"
                    width="w-24"
                    active={sortKey === "status"}
                    dir={sortDir}
                    onClick={() => toggleSort("status")}
                  />
                  <SortableTh
                    label="Date"
                    width="w-28"
                    active={sortKey === "date"}
                    dir={sortDir}
                    onClick={() => toggleSort("date")}
                  />
                  <SortableTh
                    label="Question"
                    active={sortKey === "text"}
                    dir={sortDir}
                    onClick={() => toggleSort("text")}
                  />
                  <SortableTh
                    label="Raised by"
                    width="w-32"
                    active={sortKey === "raisedBy"}
                    dir={sortDir}
                    onClick={() => toggleSort("raisedBy")}
                  />
                  <SortableTh
                    label="Assigned to"
                    width="w-32"
                    active={sortKey === "assignedTo"}
                    dir={sortDir}
                    onClick={() => toggleSort("assignedTo")}
                  />
                  <SortableTh
                    label="Related"
                    width="w-20 text-right"
                    active={sortKey === "related"}
                    dir={sortDir}
                    onClick={() => toggleSort("related")}
                  />
                </tr>
              </thead>
              <tbody>
                {sorted.map((q, i) => (
                  <tr
                    key={q.id}
                    onClick={() => setOpenId(q.id)}
                    className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-muted/40 cursor-pointer"
                  >
                    <Td className="text-right text-muted-foreground tabular-nums">{i + 1}</Td>
                    <Td className="font-mono text-[12px] tabular-nums">
                      {q.externalKey ?? q.id.slice(0, 6)}
                    </Td>
                    <Td>
                      <span
                        className={cn(
                          "inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                          PRIORITY_TINTS[q.priority],
                        )}
                      >
                        {q.priority}
                      </span>
                    </Td>
                    <Td>
                      <span
                        className={cn(
                          "inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold",
                          q.status === "answered"
                            ? "bg-success-bg text-success"
                            : "bg-muted text-foreground/70",
                        )}
                      >
                        {q.status}
                      </span>
                    </Td>
                    <Td className="text-muted-foreground tabular-nums">
                      {new Date(q.raisedAt).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </Td>
                    <Td className="max-w-[420px]" title={q.text}>
                      <div className="truncate text-foreground/90">{q.text}</div>
                    </Td>
                    <Td
                      className="text-muted-foreground truncate"
                      title={q.raisedByPerson?.name ?? ""}
                    >
                      {q.raisedByPerson?.name ?? "—"}
                    </Td>
                    <Td
                      className="text-muted-foreground truncate"
                      title={q.assignedToPerson?.name ?? ""}
                    >
                      {q.assignedToPerson?.name ?? "—"}
                    </Td>
                    <Td className="text-right text-muted-foreground tabular-nums">
                      {q.relatedItemIds.length || "—"}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <QuestionDetailSheet
        questionId={openId}
        solutionId={solutionId}
        onOpenChange={handleOpenChange}
      />
    </div>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th className={cn("text-left px-3 py-2.5 font-semibold whitespace-nowrap", className)}>
      {children}
    </th>
  );
}

function Td({
  children,
  className,
  title,
}: {
  children: React.ReactNode;
  className?: string;
  title?: string;
}) {
  return (
    <td
      className={cn("px-3 py-2 align-middle whitespace-nowrap overflow-hidden", className)}
      title={title}
    >
      {children}
    </td>
  );
}

function SortableTh({
  label,
  width,
  active,
  dir,
  onClick,
}: {
  label: string;
  width?: string;
  active: boolean;
  dir: SortDir;
  onClick: () => void;
}) {
  return (
    <th className={cn("text-left px-3 py-2.5 font-semibold whitespace-nowrap select-none", width)}>
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-1 group cursor-pointer hover:text-foreground"
      >
        <span>{label}</span>
        <span
          className={cn(
            "transition-opacity",
            active ? "opacity-100" : "opacity-30 group-hover:opacity-60",
          )}
        >
          {active && dir === "asc" ? (
            <ArrowUp className="h-3 w-3" />
          ) : active && dir === "desc" ? (
            <ArrowDown className="h-3 w-3" />
          ) : (
            <ArrowUpDown className="h-3 w-3" />
          )}
        </span>
      </button>
    </th>
  );
}

function sortQuestions(items: QuestionRow[], key: SortKey | null, dir: SortDir): QuestionRow[] {
  if (!key) return items;
  const sign = dir === "asc" ? 1 : -1;
  return [...items].sort((a, b) => {
    const va = sortValue(a, key);
    const vb = sortValue(b, key);
    if (typeof va === "number" && typeof vb === "number") return (va - vb) * sign;
    return String(va).localeCompare(String(vb)) * sign;
  });
}

function sortValue(q: QuestionRow, key: SortKey): string | number {
  switch (key) {
    case "id":
      return q.externalKey ?? q.id;
    case "priority":
      return PRIORITY_RANK[q.priority];
    case "status":
      return q.status;
    case "date":
      return new Date(q.raisedAt).getTime();
    case "text":
      return q.text;
    case "raisedBy":
      return q.raisedByPerson?.name ?? "";
    case "assignedTo":
      return q.assignedToPerson?.name ?? "";
    case "related":
      return q.relatedItemIds.length;
  }
}
